import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { useConfirm } from "../../components/Dialog";
import { useAuth } from "../../context/AuthContext";
import {
  BROKER_STATUSES,
  BROKER_STATUS_LABELS,
  BROKER_STATUS_STYLES,
  BrokerListResponse,
  BrokerListRow,
  BrokerStatus,
} from "../../types/brokers";
import Pagination from "../OnboardingCustomers/components/Pagination";
import BrokerRowActions from "./components/BrokerRowActions";

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const ALL_MARKETS = ["ES", "FR", "IT", "AXA_WL"] as const;
type MarketToken = (typeof ALL_MARKETS)[number];

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function Brokers() {
  const { token } = useAuth();
  const confirm = useConfirm();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<BrokerStatus[]>([]);
  const [selectedMarkets, setSelectedMarkets] = useState<MarketToken[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [data, setData] = useState<BrokerListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    params.set("pageSize", String(pageSize));
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    if (selectedStatuses.length > 0) {
      params.set("status", selectedStatuses.join(","));
    }
    if (selectedMarkets.length > 0) {
      params.set("market", selectedMarkets.join(","));
    }
    try {
      const response = await request<BrokerListResponse>(
        `/api/admin/operations/brokers/list?${params.toString()}`,
        { token },
      );
      setData(response);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar",
      );
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, debouncedSearch, selectedStatuses, selectedMarkets]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset pagination whenever filters change so the user doesn't end up on an
  // out-of-bounds page after narrowing the result set.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, selectedStatuses, selectedMarkets, pageSize]);

  const toggleStatus = (status: BrokerStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  };

  const toggleMarket = (market: MarketToken) => {
    setSelectedMarkets((prev) =>
      prev.includes(market) ? prev.filter((m) => m !== market) : [...prev, market],
    );
  };

  const total = data?.total ?? 0;
  const rows = data?.items ?? [];

  const statsByStatus = useMemo(() => {
    const counts: Record<BrokerStatus, number> = {
      active: 0,
      activation_pending: 0,
      questionnaire_pending: 0,
      disabled: 0,
    };
    rows.forEach((r) => {
      counts[r.status] = (counts[r.status] ?? 0) + 1;
    });
    return counts;
  }, [rows]);

  return (
    <div style={{ padding: "1.5rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.1rem", color: "#2A3F54" }}>
          Brokers
        </h1>
        <span style={{ color: "#73879C", fontSize: ".82rem" }}>
          {loading ? "Cargando…" : `${total} brokers`}
        </span>
        {!loading && rows.length > 0 && (
          <span style={{ color: "#73879C", fontSize: ".75rem" }}>
            (en página: {statsByStatus.active}A ·{" "}
            {statsByStatus.activation_pending}P ·{" "}
            {statsByStatus.questionnaire_pending}Q · {statsByStatus.disabled}D)
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.6rem",
          marginBottom: "1rem",
          background: "white",
          padding: "0.75rem 0.9rem",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        }}
      >
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, teléfono, CIF, DGSFP, business name o email empleado…"
            style={inputStyle}
          />
          {(search ||
            selectedStatuses.length > 0 ||
            selectedMarkets.length > 0) && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setSelectedStatuses([]);
                setSelectedMarkets([]);
              }}
              style={ghostBtnStyle}
            >
              Limpiar
            </button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.4rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span style={filterLabelStyle}>Estado</span>
          {BROKER_STATUSES.map((s) => {
            const active = selectedStatuses.includes(s);
            const style = BROKER_STATUS_STYLES[s];
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                style={{
                  ...chipStyle,
                  ...(active
                    ? {
                        background: style.bg,
                        color: style.fg,
                        border: `1px solid ${style.border}`,
                      }
                    : {}),
                }}
              >
                {BROKER_STATUS_LABELS[s]}
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.4rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span style={filterLabelStyle}>Mercado</span>
          {ALL_MARKETS.map((m) => {
            const active = selectedMarkets.includes(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => toggleMarket(m)}
                style={{
                  ...chipStyle,
                  ...(active
                    ? {
                        background: "#cfe2ff",
                        color: "#084298",
                        border: "1px solid #b6d4fe",
                      }
                    : {}),
                }}
              >
                {m}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            marginBottom: "1rem",
            fontSize: ".85rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: ".82rem",
          }}
        >
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th>#</Th>
              <Th>Broker</Th>
              <Th>Identificación</Th>
              <Th>Contacto</Th>
              <Th>Owner</Th>
              <Th>Mercados</Th>
              <Th>Estado</Th>
              <Th>Stats</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    color: "#73879C",
                  }}
                >
                  Sin resultados.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <BrokerRow
                key={row.brokerInternalId}
                row={row}
                token={token}
                confirm={confirm}
                onReload={load}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "1rem" }}>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
          }}
          disabled={loading}
        />
      </div>
    </div>
  );
}

function BrokerRow({
  row,
  token,
  confirm,
  onReload,
}: {
  row: BrokerListRow;
  token: string | null;
  confirm: ReturnType<typeof useConfirm>;
  onReload: () => void;
}) {
  const statusStyle = BROKER_STATUS_STYLES[row.status];
  return (
    <tr style={{ borderTop: "1px solid #f1f3f5", verticalAlign: "top" }}>
      <Td>
        <span style={{ color: "#73879C", fontFamily: "monospace" }}>
          {row.brokerInternalId}
        </span>
      </Td>
      <Td>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
          <Link
            to={`/brokers/${row.brokerInternalId}`}
            style={{ color: "#0069d9", fontWeight: 600, textDecoration: "none" }}
          >
            {row.brokerName}
          </Link>
          {row.businessName && row.businessName !== row.brokerName && (
            <span style={{ color: "#73879C", fontSize: ".75rem" }}>
              {row.businessName}
            </span>
          )}
          {row.externalId && (
            <span
              style={{
                color: "#73879C",
                fontSize: ".7rem",
                fontFamily: "monospace",
              }}
              title="External ID"
            >
              ext: {row.externalId}
            </span>
          )}
        </div>
      </Td>
      <Td>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
          <span style={{ fontFamily: "monospace", fontSize: ".78rem" }}>
            {row.brokerIdCard || "—"}
          </span>
          <span
            style={{
              color: "#73879C",
              fontSize: ".7rem",
              fontFamily: "monospace",
            }}
          >
            DGSFP {row.brokerIdDgsfp || "—"}
          </span>
        </div>
      </Td>
      <Td>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
          {row.brokerEmail ? (
            <a
              href={`mailto:${row.brokerEmail}`}
              style={{ color: "#0069d9", textDecoration: "none" }}
            >
              {row.brokerEmail}
            </a>
          ) : (
            <span style={{ color: "#adb5bd" }}>—</span>
          )}
          {row.brokerPhone && (
            <a
              href={`tel:${row.brokerPhone}`}
              style={{ color: "#0069d9", textDecoration: "none", fontSize: ".75rem" }}
            >
              {row.brokerPhone}
            </a>
          )}
        </div>
      </Td>
      <Td>
        {row.ownerEmployee ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
            <span style={{ fontSize: ".78rem" }}>
              {[row.ownerEmployee.name, row.ownerEmployee.lastname]
                .filter(Boolean)
                .join(" ") || "—"}
            </span>
            {row.ownerEmployee.email && (
              <a
                href={`mailto:${row.ownerEmployee.email}`}
                style={{ color: "#0069d9", textDecoration: "none", fontSize: ".72rem" }}
              >
                {row.ownerEmployee.email}
              </a>
            )}
            <span
              style={{ color: "#73879C", fontSize: ".7rem" }}
              title={row.ownerEmployee.lastSeenAt ?? "Nunca"}
            >
              Último acceso: {fmtDate(row.ownerEmployee.lastSeenAt)}
            </span>
          </div>
        ) : (
          <span style={{ color: "#adb5bd" }}>Sin owner</span>
        )}
      </Td>
      <Td>
        {row.markets.length === 0 ? (
          <span style={{ color: "#adb5bd" }}>—</span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.2rem" }}>
            {row.markets.map((m) => (
              <span
                key={m}
                style={{
                  background: "#e9ecef",
                  color: "#495057",
                  fontSize: ".68rem",
                  fontWeight: 600,
                  padding: "0.1rem 0.4rem",
                  borderRadius: "0.35rem",
                  fontFamily: "monospace",
                }}
              >
                {m}
              </span>
            ))}
          </div>
        )}
      </Td>
      <Td>
        <span
          style={{
            display: "inline-block",
            padding: "0.18rem 0.5rem",
            borderRadius: "0.4rem",
            fontSize: ".7rem",
            fontWeight: 600,
            background: statusStyle.bg,
            color: statusStyle.fg,
            border: `1px solid ${statusStyle.border}`,
          }}
          title={
            row.questionnaireCompletedAt
              ? `Cuestionario: ${fmtDate(row.questionnaireCompletedAt)}`
              : undefined
          }
        >
          {BROKER_STATUS_LABELS[row.status]}
        </span>
      </Td>
      <Td>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
          <span style={{ fontSize: ".75rem" }}>
            <strong>{row.employeesEnabledCount}</strong> empleados
          </span>
          <span style={{ fontSize: ".75rem" }}>
            <strong>{row.leadsCount}</strong> leads
          </span>
          {row.medorCode && (
            <span
              style={{
                fontSize: ".72rem",
                color: "#73879C",
                fontFamily: "monospace",
              }}
              title="Código MEDOR (AXA producer code)"
            >
              MEDOR {row.medorCode}
            </span>
          )}
        </div>
      </Td>
      <Td>
        <BrokerRowActions
          row={row}
          token={token}
          confirm={confirm}
          onReload={onReload}
        />
      </Td>
    </tr>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "0.6rem 0.7rem",
        textAlign: "left",
        fontWeight: 600,
        color: "#1f2933",
        fontSize: ".7rem",
        textTransform: "uppercase",
        letterSpacing: ".3px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td
      style={{
        padding: "0.55rem 0.7rem",
      }}
    >
      {children}
    </td>
  );
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: "0.45rem 0.6rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".82rem",
};

const chipStyle: React.CSSProperties = {
  padding: "0.25rem 0.55rem",
  borderRadius: "0.35rem",
  fontSize: ".72rem",
  fontWeight: 600,
  border: "1px solid #dee2e6",
  background: "white",
  color: "#73879C",
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  padding: "0.4rem 0.7rem",
  borderRadius: "0.35rem",
  border: "1px solid #dee2e6",
  background: "white",
  color: "#73879C",
  fontSize: ".75rem",
  fontWeight: 600,
  cursor: "pointer",
};

const filterLabelStyle: React.CSSProperties = {
  fontSize: ".7rem",
  textTransform: "uppercase",
  letterSpacing: ".3px",
  fontWeight: 700,
  color: "#73879C",
  marginRight: "0.3rem",
};
